import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Upload, FileCheck2, X } from 'lucide-react';

function formatWhen(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function formatSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Drag-and-drop file picker with optional saved docs/ indicator.
 * @param {object} savedFile - { name, modifiedAt, size } from /api/docs/inputs
 */
export default function FileUploadField({
  label,
  hint,
  accept,
  file,
  onChange,
  savedFile = null,
  optional = false,
  helpTerm,
  HelpTipComponent,
}) {
  const inputId = useId();
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const pickFile = useCallback(
    (next) => {
      onChange?.(next || null);
    },
    [onChange]
  );

  const onDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragOver(false);
      const dropped = e.dataTransfer.files?.[0];
      if (dropped) pickFile(dropped);
    },
    [pickFile]
  );

  useEffect(() => {
    const prevent = (e) => e.preventDefault();
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
    };
  }, []);

  const usingSaved = !file && !!savedFile?.name;

  return (
    <div
      className={`file-upload-field ${dragOver ? 'file-upload-field-drag' : ''} ${usingSaved ? 'file-upload-field-saved' : ''}`}
      onDragEnter={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-medium text-slate-200">{label}</span>
            {optional && <span className="text-[10px] uppercase tracking-wide text-slate-500">Optional</span>}
            {helpTerm && HelpTipComponent ? <HelpTipComponent term={helpTerm} /> : null}
          </div>
          {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
        </div>
        {file && (
          <button
            type="button"
            className="rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-slate-300"
            title="Clear upload"
            onClick={() => {
              pickFile(null);
              if (inputRef.current) inputRef.current.value = '';
            }}
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {usingSaved && (
        <div className="file-upload-saved-badge">
          <FileCheck2 className="h-4 w-4 shrink-0 text-emerald-400" />
          <div className="min-w-0">
            <span className="block truncate text-xs font-medium text-emerald-300">
              Using saved file
            </span>
            <span className="block truncate font-mono text-[11px] text-slate-400">{savedFile.name}</span>
            <span className="block text-[10px] text-slate-500">
              {formatWhen(savedFile.modifiedAt)}
              {savedFile.size ? ` · ${formatSize(savedFile.size)}` : ''}
            </span>
          </div>
        </div>
      )}

      <label htmlFor={inputId} className="file-upload-dropzone">
        <Upload className="h-5 w-5 shrink-0 text-cyan-500/80" />
        <span className="text-sm text-slate-300">
          {file ? (
            <>
              Selected: <span className="font-mono text-emerald-400">{file.name}</span>
            </>
          ) : (
            <>Drag & drop or click to upload{usingSaved ? ' (overrides saved file)' : ''}</>
          )}
        </span>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          className="sr-only"
          onChange={(e) => pickFile(e.target.files?.[0] || null)}
        />
      </label>
    </div>
  );
}
